import axios from 'axios';
import { getConfig } from '../config/environment';

// Use runtime environment detection to switch between dev and prod APIs
const config = getConfig();
const API_BASE_URL = config.apiUrl;

interface LoginResponse {
  user: {
    user_id: string;
    tenant_id: string;
    email: string;
    org_slug?: string;
    role: string;
    permissions: string[];
    is_admin: boolean;
  };
  token: string;
  requires_password_change?: boolean;
}

// interface LoginRequest {
//   email: string;
//   password: string;
// }

class AuthService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      // Use form-encoded to avoid CORS preflight (API Gateway HTTP API v2 has issues with preflight)
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('password', password);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit', // Don't send credentials to avoid CORS issues
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      const { token, user, requires_password_change } = data;
      
      // Store token for future requests
      localStorage.setItem('auth_token', token);
      
      return { user, token, requires_password_change };
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(errorData.detail || 'Failed to send reset email');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(error.message || 'Failed to send reset email');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Reset failed' }));
        throw new Error(errorData.detail || 'Failed to reset password');
      }

      return await response.json();
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<LoginResponse> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        }),
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to change password' }));
        throw new Error(errorData.detail || 'Failed to change password');
      }

      const data = await response.json();
      const { token: newToken, user } = data;
      
      // Store new token
      localStorage.setItem('auth_token', newToken);
      
      return { user, token: newToken };
    } catch (error: any) {
      console.error('Change password error:', error);
      throw new Error(error.message || 'Failed to change password');
    }
  }

  async validateToken(token: string): Promise<any> {
    try {
      // Validate token with server using fetch
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      const data = await response.json();
      return data.user;
    } catch (error) {
      throw new Error('Invalid token');
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        credentials: 'omit',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Token refresh failed' }));
        throw new Error(errorData.detail || 'Token refresh failed');
      }

      const data = await response.json();
      return data.token;
    } catch (error: any) {
      throw new Error(error.message || 'Token refresh failed');
    }
  }

  logout(): void {
    localStorage.removeItem('auth_token');
  }

  // Demo login function - creates a mock JWT token
  private async createDemoLogin(email: string, password: string): Promise<LoginResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Simple demo validation
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    // Determine tenant based on email domain
    const domain = email.split('@')[1];
    let tenant_id = 'demo';
    // let tenant_name = 'Demo Company';

    if (domain === 'acme.com') {
      tenant_id = 'acme-corp';
      // tenant_name = 'ACME Corporation';
    } else if (domain === 'startup.com') {
      tenant_id = 'startup-inc';
      // tenant_name = 'Startup Inc';
    }

    const user = {
      user_id: `user_${Date.now()}`,
      tenant_id,
      email,
      role: email.includes('admin') ? 'admin' : 'user',
      permissions: email.includes('admin') ? ['chat', 'admin', 'analytics'] : ['chat'],
      is_admin: email.includes('admin'),
    };

    // Use a working JWT token that matches the Lambda backend
    const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiZGVtb191c2VyXzEyMyIsInRlbmFudF9pZCI6ImRlbW8iLCJlbWFpbCI6ImRlbW9AZXhhbXBsZS5jb20iLCJyb2xlIjoidXNlciIsInBlcm1pc3Npb25zIjpbImNoYXQiXSwiaXNfYWRtaW4iOmZhbHNlLCJpYXQiOjE3NTg2NTcxNDIsImV4cCI6MTc1ODc0MzU0Mn0.a1qbZp_fvYlxQqK2pwSY7yAxtjLWeltqRcH7YX-EMms';

    return { user, token };
  }

  private createMockJWT(user: any): string {
    // Return a pre-signed JWT that matches the Lambda's JWT_SECRET_KEY
    return 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiZGVtb191c2VyIiwidGVuYW50X2lkIjoiZGVtbyIsImVtYWlsIjoiZGVtb0BleGFtcGxlLmNvbSIsInJvbGUiOiJ1c2VyIiwicGVybWlzc2lvbnMiOlsiY2hhdCJdLCJpc19hZG1pbiI6ZmFsc2UsImlhdCI6MTc1ODE0NjQ3MiwiZXhwIjoxNzU4MjMyODcyfQ.KtyijF0kNImpHQCx4gkeUS0C1qK5fcRUl8Du37CsgAU';
  }

  private decodeJWT(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(parts[1]));
      return payload;
    } catch (error) {
      throw new Error('Failed to decode token');
    }
  }

  private isTokenExpired(payload: any): boolean {
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  }
}

export const authService = new AuthService();
